import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { env } from 'process';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';

// Constants
const DEBOUNCE_TIME = 500;
const MAX_PREDICTIONS = 3;

// Global state for managing z-index layering
let globalZIndexCounter = 9999;
const activeInputs = new Set();

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const AddressInput = ({
  onAddressSelect,
  initialValue = '',
  useGoogle = true,
}) => {
  const [input, setInput] = useState(initialValue);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [googleAutocompleteService, setGoogleAutocompleteService] = useState(null);
  const [googleGeocoderService, setGoogleGeocoderService] = useState(null);
  const [googleApiReady, setGoogleApiReady] = useState(false);
  const [zIndex, setZIndex] = useState(9999);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const inputRef = useRef(null);
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));

  // Load Google Maps API using the useJsApiLoader hook
  const { isLoaded, loadError } = useGoogleMaps();

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Initialize Google services when API is loaded
  useEffect(() => {
    if (isLoaded && useGoogle && !googleApiReady) {
      try {
        // Create a small delay to ensure the API is fully initialized
        const timer = setTimeout(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            setGoogleAutocompleteService(new window.google.maps.places.AutocompleteService());
            setGoogleGeocoderService(new window.google.maps.Geocoder());
            setGoogleApiReady(true);
            console.log("Google Maps API services initialized successfully");
          } else {
            console.error("Google Maps API not fully loaded");
          }
        }, 500);
        
        return () => clearTimeout(timer);
      } catch (error) {
        console.error("Error initializing Google services:", error);
      }
    }
  }, [isLoaded, useGoogle, googleApiReady]);

  // Log loading errors
  useEffect(() => {
    if (loadError) {
      console.error("Error loading Google Maps API:", loadError);
    }
  }, [loadError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      activeInputs.delete(instanceId.current);
    };
  }, []);

  // Update position when suggestions are shown
  useEffect(() => {
    if (showSuggestions) {
      updateDropdownPosition();
      // Update position on scroll and resize
      const handlePositionUpdate = () => updateDropdownPosition();
      window.addEventListener('scroll', handlePositionUpdate, true);
      window.addEventListener('resize', handlePositionUpdate);
      
      return () => {
        window.removeEventListener('scroll', handlePositionUpdate, true);
        window.removeEventListener('resize', handlePositionUpdate);
      };
    }
  }, [showSuggestions]);

  // Handle focus to bring this input to the front
  const handleInputFocus = () => {
    globalZIndexCounter += 1;
    setZIndex(globalZIndexCounter);
    activeInputs.add(instanceId.current);
    setShowSuggestions(true);
    updateDropdownPosition();
  };

  // Handle blur to potentially hide suggestions
  const handleInputBlur = () => {
    // Don't hide immediately to allow for suggestion clicks
    setTimeout(() => {
      if (!inputRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        activeInputs.delete(instanceId.current);
      }
    }, 150);
  };

  // Format full address for suggestions
  const formatAddress = (address) => {
    if (useGoogle) {
      // Format Google address components
      const parts = [];
      const streetNumber = address.street_number || '';
      const street = address.street || '';
      if (streetNumber && street) {
        parts.push(`${streetNumber} ${street}`);
      } else if (street) {
        parts.push(street);
      }

      if (address.locality) parts.push(address.locality);
      if (address.administrative_area_level_1) parts.push(address.administrative_area_level_1);
      if (address.postal_code) parts.push(address.postal_code);
      if (address.country) parts.push(address.country);

      return parts.join(', ');
    } else {
      // Format Nominatim address
      const parts = [];
      if (address.house_number && address.road) {
        parts.push(`${address.house_number} ${address.road}`);
      } else if (address.road) {
        parts.push(address.road);
      }
      if (address.city) parts.push(address.city);
      if (address.state) parts.push(address.state);
      if (address.postcode) parts.push(address.postcode);
      if (address.country) parts.push(address.country);

      return parts.join(', ');
    }
  };

  // Update input when initialValue changes - now with formatting
  useEffect(() => {
    if (initialValue) {
      // Set the input to show the initial value
      setInput(initialValue);
    }
  }, [initialValue]);

  const promisePool = async (tasks, poolLimit) => {
    const results = [];
    const executing = [];

    for (const task of tasks) {
      // Ensure task is a function
      if (typeof task !== 'function') {
        console.error('Task is not a function:', task);
        continue; // Skip this task
      }

      const p = task().then((result) => {
        results.push(result);
        return result; // Return result to allow proper tracking
      }).catch(error => {
        console.error('Task error:', error);
        return null; // Return null for failed tasks
      });
      
      executing.push(p);

      if (executing.length >= poolLimit) {
        await Promise.race(executing);
        // Remove completed promises from executing array
        const completedIndex = await Promise.race(
          executing.map(async (p, index) => {
            const result = await Promise.race([p, Promise.resolve('pending')]);
            return result === 'pending' ? -1 : index;
          })
        );
        if (completedIndex !== -1) {
          executing.splice(completedIndex, 1);
        }
      }
    }

    await Promise.all(executing);
    return results;
  };

  // Fetch predictions from Google Places API
  const fetchGooglePredictions = async (input) => {
    if (!googleApiReady || !googleAutocompleteService) {
      console.error('Google Autocomplete Service not initialized');
      return [];
    }

    try {
      setLoading(true);
      
      // Create a task function that returns a promise
      const getPredictionTask = () => {
        return new Promise((resolve, reject) => {
          googleAutocompleteService.getPlacePredictions(
            { input: input, types: ['address'] },
            (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                resolve(predictions);
              } else {
                reject(new Error(`Google Places API error: ${status}`));
              }
            }
          );
        });
      };
      
      // Create an array with a single task function
      const tasks = [getPredictionTask];
      
      const results = await promisePool(tasks, 1);
      
      // Process the predictions
      const formattedPredictions = results[0]?.map(prediction => ({
        id: prediction.place_id,
        display_name: prediction.description,
        address: {
          // You'll need to implement a function to parse Google's address format
          // This is a simplified example
          street: prediction.structured_formatting?.main_text || '',
          city: prediction.structured_formatting?.secondary_text || '',
        },
        // You'll need to get geometry in a separate call
        geometry: null
      })) || [];
      
      setPredictions(formattedPredictions);
      return formattedPredictions;
    } catch (error) {
      console.error('Error fetching Google predictions:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch address details including geometry
  const fetchAddressDetails = async (placeId) => {
    if (!googleGeocoderService) {
      console.error('Google Geocoder Service not initialized');
      return null;
    }

    try {
      // Create a task function that returns a promise
      const getDetailsTask = () => {
        return new Promise((resolve, reject) => {
          googleGeocoderService.geocode(
            { placeId: placeId },
            (results, status) => {
              if (status === window.google.maps.GeocoderStatus.OK && results && results.length > 0) {
                resolve(results[0]);
              } else {
                reject(new Error(`Google Geocoder API error: ${status}`));
              }
            }
          );
        });
      };
      
      // Create an array with a single task function
      const tasks = [getDetailsTask];
      
      const results = await promisePool(tasks, 1);
      return results[0];
    } catch (error) {
      console.error('Error fetching address details:', error);
      return null;
    }
  };

  // Main fetch function that decides which API to use
  const fetchPredictions = async (input) => {
    if (!input.trim()) {
      setPredictions([]);
      return;
    }
    setLoading(true);
    await fetchGooglePredictions(input);
    setLoading(false);
  };

  // Debounce the API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPredictions(input);
    }, DEBOUNCE_TIME);

    return () => clearTimeout(timeoutId);
  }, [input, useGoogle]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    
    if (value.length > 2) {
      fetchGooglePredictions(value);
      setShowSuggestions(true);
    } else {
      setPredictions([]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
        // Check if click is outside both the input and the suggestions dropdown
        if (inputRef.current && !inputRef.current.contains(event.target)) {
            // Also check if the click is not on a suggestion item
            const isClickOnSuggestion = event.target.closest('[data-suggestion-dropdown]');
            if (!isClickOnSuggestion) {
                setShowSuggestions(false);
            }
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle selecting an address from suggestions
  const handleSelectAddress = async (prediction) => {
    // Store the original prediction text that the user clicked on
    const originalPredictionText = prediction.display_name;
    
    // If using Google API, fetch detailed information including geometry
    if (useGoogle && prediction.id) {
      const details = await fetchAddressDetails(prediction.id);
      if (details) {
        const location = details.geometry.location;
        
        // Update prediction with geometry
        prediction.geometry = {
          lat: location.lat(),
          lng: location.lng()
        };
        
        // Parse address components
        const addressComponents = {};
        details.address_components.forEach(component => {
          const type = component.types[0];
          addressComponents[type] = component.long_name;
        });
        
        prediction.address = {
          street_number: addressComponents.street_number || '',
          route: addressComponents.route || '',
          locality: addressComponents.locality || addressComponents.administrative_area_level_2 || '',
          administrative_area_level_1: addressComponents.administrative_area_level_1 || '',
          country: addressComponents.country || '',
          postal_code: addressComponents.postal_code || ''
        };
      }
    }
    
    // Use the original prediction text that the user clicked on
    setInput(originalPredictionText);
    
    setShowSuggestions(false);
    
    if (onAddressSelect) {
      // Pass the original prediction text that the user selected
      onAddressSelect(originalPredictionText, prediction.geometry, originalPredictionText);
    }
  };

  // Render suggestions dropdown using portal
  const renderSuggestionsDropdown = () => {
    if (!showSuggestions || predictions.length === 0) return null;

    return ReactDOM.createPortal(
      <div 
        data-suggestion-dropdown="true"
        className="absolute bg-white border border-gray-300 rounded-md shadow-lg"
        style={{ 
          top: dropdownPosition.top + 4,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: zIndex,
          maxHeight: '200px',
          overflowY: 'auto'
        }}
      >
        {predictions.map((prediction) => (
          <div
            key={prediction.id}
            className="px-4 py-2 font-nunito text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
            onMouseDown={(e) => {
              // Prevent blur event from firing before click
              e.preventDefault();
            }}
            onClick={() => handleSelectAddress(prediction)}
          >
            {prediction.display_name}
          </div>
        ))}
      </div>,
      document.body
    );
  };

  return (
    <div className="relative w-full" ref={inputRef}>
      <input
        type="text"
        value={input}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        className="mt-1 block font-nunito w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        placeholder="Start typing an address..."
      />

      {loading && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <div className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
      )}

      {renderSuggestionsDropdown()}
    </div>
  );
};

export default AddressInput;